import { ChevronLeft } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast as toaster } from 'react-toastify';

import { CompetitionType } from '../../inviteFriends/InviteFriendsPage';
import { useHandlePostulantInvitations } from '../../inviteFriends/hooks/useHandlePostulantInvitations';

import { Button, InputField, Modal } from '@/components';
import { MainHome, TourneyList } from '@/components/GenericsTournaments';
import { Paginator } from '@/components/Paginator';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMinileagueData } from '@/hooks/useMinileagueData';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { useUsersProfilePic } from '@/hooks/useUsersProfilePic';
import ROUTES from '@/router/routes';
import {
  basicDataMinileague,
  inviteMinileagueMember,
  listParticipantsMinileague,
  manageMLRequest,
  pushOutMinileagueMember,
} from '@/services/minileagues';
import {
  DatosParticipanteMiniLigasTO,
  ResponseListParticipatingMinileague,
} from '@/types/minileagues';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextGDT, EnumContextTourneys } from '@/types/team';
import { cn, scrollSmooth, TEXTS, uppercaseFirstLetter } from '@/utils';

interface RowProps {
  actions: 'resquesting' | 'inviting' | 'removing';
  isLastItem?: boolean;
  id: number;
  name: string;
  lastName: string;
  teamName: string;
  photo: string;
  userId: number;
  minileagueId: number;
  userData: DatosParticipanteMiniLigasTO;
  handlePushOut?: (idDT: number) => void;
  refetchMembers?: () => void;
  onShowRequest?: (user: DatosParticipanteMiniLigasTO) => void;
}

const enum EnumEstadoInvitacion {
  PENDIENTE_ACEPTAR_DUENIO = 1,
  PARTICIPANDO = 2,
  ELIMINADO = 3,
  RECHAZADO = 4,
  PENDIENTE_ACEPTAR_INVITADO = 5,
  RECHAZA_INVITADO = 6,
}

const initialState = {
  showModalRequest: false,
  showModalExpel: false,
  nameUser: '',
  idUser: 0,
};

const Row = ({
  actions,
  id,
  userId,
  minileagueId,
  name,
  lastName,
  teamName,
  photo,
  isLastItem,
  userData,
  handlePushOut,
  refetchMembers,
  onShowRequest,
}: RowProps) => {
  const { userInitialData } = useAuth();
  const toast = useToast();
  const { tourneyData } = useTourney();
  const { managePostulantExecutor, member } = useHandlePostulantInvitations({
    type: CompetitionType.MINILEAGUES,
    idTorneo: minileagueId,
    onInvite: async (): Promise<boolean | undefined> => false,
    tourneyData,
  });

  const { setProfilePic } = useUsersProfilePic();

  useEffect(() => {
    if (member) {
      refetchMembers && refetchMembers();
    }
  }, [member]);

  const handleSendInvitation = async () => {
    try {
      const response = await listParticipantsMinileague(minileagueId);
      const isPartPend = response.miniLigaPartPend?.some(
        (player: DatosParticipanteMiniLigasTO) => {
          return player.part?.idUsuario === userId;
        }
      );
      if (isPartPend) {
        managePostulantExecutor(minileagueId, userId);
      } else {
        inviteMinileagueMember(minileagueId, userId).finally(() => {
          toast.show('La invitación fue enviada exitosamente.');
          refetchMembers && refetchMembers();
        });
      }
    } catch (error: any) {
      toast.show(error.message);
    }
  };

  const acceptDeclineRequest = (action: 'accept' | 'decline') => {
    manageMLRequest(minileagueId, userId, action === 'accept' ? 1 : 2)
      .catch((error) => {
        toast.show(error.message || 'Error del sistema');
      })
      .finally(() => {
        refetchMembers && refetchMembers();
      });
  };

  return (
    <div
      className={cn(
        'flex justify-between items-center bg-white p-6 pl-4 w-full',
        {
          'border-b border-gray-300': !isLastItem, // <-- NUEVO
          'rounded-t-lg': id === 0,
          'rounded-b-lg': isLastItem,
        }
      )}
    >
      <div className="flex items-center gap-3">
        {setProfilePic({
          alt: lastName,
          dtName:
            uppercaseFirstLetter(name) + ' ' + uppercaseFirstLetter(lastName),
          url: photo,
          size: 40,
        })}
        <div className="flex flex-col gap-y-1.5">
          <span className="font-bold text-xs md:text-sm">
            {name} {lastName}
          </span>
          <span className="font-semibold text-gdt-gray-2 text-3xs md:text-xs">
            {teamName}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        {actions === 'removing' && (
          <Button
            className="w-[115px] h-[36px] px-0 text-xs font-bold"
            disabled={userInitialData?.idUsuarioDT === userId}
            type="button"
            variant="danger"
            onClick={() => handlePushOut && handlePushOut(userId)}
          >
            Expulsar
          </Button>
        )}
        {actions === 'resquesting' && (
          <>
            <div className="flex gap-2 max-md:hidden">
              <Button
                className="w-[84px] h-[36px] px-0 text-xs"
                type="button"
                variant="danger"
                onClick={() => acceptDeclineRequest('decline')}
              >
                Rechazar
              </Button>
              <Button
                className="w-[84px] h-[36px] px-0 text-xs"
                type="button"
                variant="secondary"
                onClick={() => acceptDeclineRequest('accept')}
              >
                Aceptar
              </Button>
            </div>
            <Button
              className="md:hidden text-xs lea"
              type="button"
              variant="secondary"
              onClick={() => onShowRequest && onShowRequest(userData)}
            >
              Ver solicitud
            </Button>
          </>
        )}
        {actions === 'inviting' && (
          <Button
            className="w-[115px] h-[36px] px-0 text-xs"
            disabled={[
              EnumEstadoInvitacion.PENDIENTE_ACEPTAR_DUENIO,
              EnumEstadoInvitacion.PARTICIPANDO,
              EnumEstadoInvitacion.ELIMINADO,
              EnumEstadoInvitacion.RECHAZADO,
              EnumEstadoInvitacion.PENDIENTE_ACEPTAR_INVITADO,
            ].includes(Number(userData?.idEstadoParticipacion) ?? 0)}
            type="button"
            variant="primary"
            onClick={handleSendInvitation}
          >
            Reenviar
          </Button>
        )}
      </div>
    </div>
  );
};

interface ListProps {
  items?: DatosParticipanteMiniLigasTO[];
  type?: 'resquesting' | 'inviting' | 'removing';
  minileagueId: number;
  handlePushOut?: (idDt: number) => void;
  refetchMembers?: () => void;
  onShowRequest?: (user: DatosParticipanteMiniLigasTO) => void; // NEW
}

const List = ({
  items,
  type = 'removing',
  minileagueId,
  handlePushOut,
  refetchMembers,
  onShowRequest,
}: ListProps) => {
  const { isMobile } = useIsDesktop();
  const [contentCurrentPage, setContentCurrentPage] = useState<
    DatosParticipanteMiniLigasTO[]
  >(items ?? []);

  useEffect(() => {
    if (items && items.length > 0 && !isMobile) {
      setContentCurrentPage(items.slice(0, 5));
    } else {
      setContentCurrentPage(items ?? []);
    }
  }, [isMobile, items]);

  const handleContentCurrentPage = (
    players: DatosParticipanteMiniLigasTO[]
  ) => {
    players && setContentCurrentPage(players);
  };

  return (
    <div
      className={cn('flex flex-col w-full max-md:overflow-y-auto', {
        'max-md:h-96': items?.length && items.length > 5,
      })}
    >
      {contentCurrentPage.map((item, index) => (
        <Row
          key={`row-${index}`}
          actions={type}
          handlePushOut={(idDT) => handlePushOut && handlePushOut(idDT)}
          id={index}
          isLastItem={contentCurrentPage.length - 1 === index}
          lastName={item.part?.a ?? ''}
          minileagueId={minileagueId}
          name={item.part?.n ?? ''}
          photo={item.fotoPerfil ?? item.part?.fotoPerfil ?? ''}
          refetchMembers={refetchMembers}
          teamName={item.ne ?? ''}
          userData={item}
          userId={item.part?.idUsuario ?? 0}
          onShowRequest={onShowRequest}
        />
      ))}
      {(items ?? []).length > 5 && !isMobile && (
        <Paginator
          key={`paginator-${type}`}
          currentContentPage={handleContentCurrentPage}
          items={items ?? []}
          itemsPerPage={5}
        />
      )}
    </div>
  );
};

export const AdminContent = React.memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [requestToReview, setRequestToReview] =
    useState<DatosParticipanteMiniLigasTO | null>(null);
  const { isMobile } = useIsDesktop();
  const toast = useToast();
  const { updateTourney } = useTourney();
  const { updateMinileagueProperties } = useMinileagueData();
  const { userInitialData } = useAuth();

  const [state, setState] = useState(initialState);
  const { isDesktop } = useIsDesktop();

  const { data: minileagueParticipants, refetch } = useQuery(
    'listMembers',
    async () => await listParticipantsMinileague(location.state.minileagueId),
    {
      onSuccess: async (response: ResponseListParticipatingMinileague) => {
        if (response.estado) {
          updateTourney({
            minileagueMembers: response.miniLigaParticipantes,
            minileaguePartPending: response.miniLigaPartPend,
          });
          updateMinileagueProperties({
            miniLiga: {
              length: 1,
              id: location.state.minileagueId,
              nombreML: response.nombreML,
            },
          });
        }
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { data: minileagueParticipantsInvited, refetch: refetchInvitation } =
    useQuery(
      'listInvitedMembers',
      async () =>
        await listParticipantsMinileague(location.state.minileagueId, true),
      {
        onSuccess: (response: ResponseListParticipatingMinileague) => {
          updateTourney({
            minileagueInvited: response.miniLigaPartPend,
          });
        },
        refetchOnWindowFocus: false,
        keepPreviousData: false,
      }
    );

  const handleRefetch = () => {
    refetch();
    refetchInvitation();
  };

  const { data: basicData } = useQuery(
    'basicData',
    async () => await basicDataMinileague(location.state.minileagueId),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const memberList = useMemo((): GenericItemTourneysData[] | null => {
    if (
      !minileagueParticipants?.estado ||
      minileagueParticipants?.miniLigaParticipantes?.length === 0
    )
      return null;
    return (
      minileagueParticipants?.miniLigaParticipantes?.map(
        (item: DatosParticipanteMiniLigasTO) => {
          const fullName = item.part?.n + ' ' + item.part?.a;
          return {
            id: item.part?.idUsuario,
            title: fullName,
            action: {
              txtAction: 'Expulsar',
              variantButton: 'danger',
              txtActionDisabled: 'Expulsar',
            },
            profilePicUrl:
              userInitialData?.idUsuarioDT === item.part?.idUsuario
                ? userInitialData?.fotoPerfil ||
                  userInitialData?.pathImagenPerfil
                : item.fotoPerfil,
            profileName: fullName,
            disabled: userInitialData?.idUsuarioDT === item.part?.idUsuario,
            subtitle: isMobile ? item.ne?.toLocaleUpperCase() : item.e,
          };
        }
      ) || []
    );
  }, [
    isMobile,
    minileagueParticipants?.estado,
    minileagueParticipants?.miniLigaParticipantes,
    userInitialData?.fotoPerfil,
    userInitialData?.idUsuarioDT,
    userInitialData?.pathImagenPerfil,
  ]);

  const handleAcceptDeclineRequest = (action: 'accept' | 'decline') => {
    if (!requestToReview) return;
    manageMLRequest(
      location.state.minileagueId,
      requestToReview.part?.idUsuario ?? 0,
      action === 'accept' ? 1 : 2
    )
      .catch((error) => {
        toast.show(error.message || 'Error del sistema');
      })
      .finally(() => {
        setRequestToReview(null);
        refetch();
      });
  };

  toaster.onChange((toastItem) => {
    if (toastItem.status === 'removed') {
      if (toastItem.content === 'Tu equipo está completo') {
        navigate(ROUTES.minileagues.home);
      }
    }
  });
  useEffect(() => {
    if (
      minileagueParticipants?.miniLigaParticipantes !== undefined &&
      minileagueParticipants?.miniLigaParticipantes?.length > 0 &&
      minileagueParticipants?.miniLigaParticipantes?.length ===
        location.state.maxPlayers
    ) {
      toast.show('Tu equipo está completo');
    }
  }, [
    minileagueParticipants?.miniLigaParticipantes,
    location.state.maxPlayers,
  ]);

  scrollSmooth();

  // VER MODAL EXPULSAR MIEMBRO
  const onHandleMembers = useCallback(
    async (action: ActionsTourneys) => {
      const dataUser = memberList?.find((user) => user.id === action.id);
      setState({
        showModalExpel: true,
        showModalRequest: false,
        nameUser: `${dataUser?.title}`,
        idUser: dataUser?.id || 0,
      });
    },
    [memberList]
  );

  // TODO: EXPULSAR MIEMBROS TORNEO
  const onHandleExpelMembers = async (idUser: number) => {
    try {
      const responseExpel = await pushOutMinileagueMember(
        location.state.minileagueId,
        idUser
      );

      if (!responseExpel.estado) {
        console.error(responseExpel.mensaje);
        toast.show(TEXTS.defaultError);
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    }

    setState(initialState);
    refetch();
  };

  return (
    <>
      <Button
        className="font-bold flex gap-2 text-base items-center mb-10 no-underline max-md:hidden"
        type="button"
        variant="text"
        onClick={() => {
          navigate(ROUTES.minileagues.home);
        }}
      >
        <ChevronLeft size={20} />
        Volver
      </Button>

      <div className="grid grid-cols-12 ">
        <div className="col-span-12 col-start-1 md:col-span-6">
          <MainHome
            className="w-full "
            context={EnumContextGDT.Minileagues}
            subtitle={
              isMobile
                ? ''
                : `Admin: ${basicData?.nombreDuenio ?? ''} ${
                    basicData?.apellidoDuenio
                  } - ${
                    minileagueParticipants?.miniLigaParticipantes?.length
                  } ${
                    (minileagueParticipants?.miniLigaParticipantes?.length ??
                      0) > 1
                      ? 'PARTICIPANTES'
                      : 'PARTICIPANTE'
                  }`
            }
            title="Administrar Miniliga"
          >
            <div className="flex flex-col mt-7">
              <div className="w-full md:w-1/2 px-4">
                <InputField
                  behavior="default"
                  disabled={true}
                  label="Nombre de la Miniliga"
                  maxLength={30}
                  name="name"
                  placeholder="Nombre"
                  value={minileagueParticipants?.nombreML ?? ''}
                  onChange={() => null}
                />
              </div>

              <div
                className={cn(`w-full flex flex-col items-start md:hidden`, {
                  hidden:
                    minileagueParticipants?.miniLigaPartPend?.length === 0,
                })}
              >
                <span className="font-bold mt-6 mb-3 px-4">Solicitudes</span>
                <List
                  items={minileagueParticipants?.miniLigaPartPend}
                  minileagueId={location.state.minileagueId}
                  refetchMembers={handleRefetch}
                  type="resquesting"
                  onShowRequest={setRequestToReview}
                />
              </div>

              <div className="w-full md:hidden pt-5">
                <div className="flex flex-col items-center px-10 gap-y-3">
                  <Button
                    className="font-bold"
                    type="button"
                    variant="text"
                    onClick={() => {
                      navigate(ROUTES.minileagues.invite);
                    }}
                  >
                    Invitar amigos
                  </Button>
                </div>
              </div>

              {/* <List
                handlePushOut={setMemberToRemove}
                items={minileagueParticipants?.miniLigaParticipantes}
                minileagueId={location.state.minileagueId}
                refetchMembers={handleRefetch}
              /> */}
              {/* MIEMBROS TORNEO */}
              {memberList && (
                <TourneyList
                  key={`${minileagueParticipants?.miniLigaParticipantes?.length}-members-list`}
                  className="lg:mt-9 mt-6"
                  context={EnumContextTourneys.Admin}
                  handleSubmit={(action) => {
                    action.id && onHandleMembers(action);
                  }}
                  list={memberList}
                  paginator={isDesktop}
                  title={<p className="px-4">Miembros</p>}
                  variant="default"
                  withAvatar={true}
                />
              )}
              {minileagueParticipantsInvited?.miniLigaPartPend?.length ? (
                <>
                  <span className="font-bold mb-3 mt-6 px-4">
                    DT&apos;s invitados
                  </span>
                  <List
                    items={minileagueParticipantsInvited?.miniLigaPartPend}
                    minileagueId={location.state.minileagueId}
                    refetchMembers={handleRefetch}
                    type="inviting"
                  />
                </>
              ) : null}
            </div>
          </MainHome>
        </div>
        <div className="flex-col col-span-5 items-end col-start-8 hidden md:flex">
          <div className="w-[203px] h-[50px]">
            <Button
              className="w-full h-full"
              type="button"
              variant="secondary"
              onClick={() => {
                navigate(ROUTES.minileagues.invite);
              }}
            >
              Invitar amigos
            </Button>
          </div>
          <div
            className={cn(`w-full flex flex-col items-start mb-14 mt-4`, {
              hidden: minileagueParticipants?.miniLigaPartPend?.length === 0,
            })}
          >
            <span className="font-bold mt-6 mb-3">Solicitudes</span>
            <List
              items={minileagueParticipants?.miniLigaPartPend}
              minileagueId={location.state.minileagueId}
              refetchMembers={handleRefetch}
              type="resquesting"
            />
          </div>
        </div>
      </div>

      <Modal
        controlledOnClose={() =>
          setState((prev) => {
            return { ...prev, showModalExpel: false };
          })
        }
        isControlledOpen={state.showModalExpel}
        render={() => {
          return (
            <div className="flex flex-col gap-4 py-5 md:py-0 items-center">
              <span className="text-center md:text-start text-sm font-bold text-gdt-gray-3 pb-3 md:pb-0">
                ¿Estás seguro que deseás EXPULSARLO?
              </span>
              <div className="flex justify-end gap-2">
                <Button
                  className="w-full md:w-[115px] md:h-[36px] md:px-0 md:text-xs"
                  type="button"
                  variant="danger"
                  onClick={() => onHandleExpelMembers(state.idUser)}
                >
                  Aceptar
                </Button>
                <Button
                  className="w-full md:w-[115px] md:h-[36px] md:px-0 md:text-xs"
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setState((prev) => {
                      return { ...prev, showModalExpel: false };
                    })
                  }
                >
                  Cancelar
                </Button>
              </div>
            </div>
          );
        }}
        size="xs"
        variant="controlled"
      />
      <Modal
        controlledOnClose={() => setRequestToReview(null)}
        isControlledOpen={!!requestToReview}
        render={() =>
          requestToReview && (
            <div className="flex flex-col gap-4 my-5">
              <span className="text-lg font-normal text-center">
                <strong>
                  {requestToReview.part?.n} {requestToReview.part?.a}
                </strong>{' '}
                quiere unirse a tu Miniliga
              </span>
              <div className="flex justify-end gap-2 w-full">
                <Button
                  className="w-full"
                  type="button"
                  variant="secondary"
                  onClick={() => handleAcceptDeclineRequest('decline')}
                >
                  Rechazar
                </Button>
                <Button
                  className="w-full"
                  type="button"
                  variant="primary"
                  onClick={() => handleAcceptDeclineRequest('accept')}
                >
                  Aceptar
                </Button>
              </div>
            </div>
          )
        }
        size="2xs"
        variant="controlled"
      />
    </>
  );
});

AdminContent.displayName = 'AdminContent';
